import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDecisionNodeAtPath, getSimplrAccountIdOfSelectedTicket } from '../../../../extractFromState';
import { Title, TopContainer } from '../../DecisionTree.styles';
import { handleError } from '../../../../services/ErrorService/ErrorService';
import { Container, InnerContainer, SubText, SubmitButton } from './DTPauseSubscriptionNode.styles';
import SearchDropDownComponent from '../../../DropDown/SearchDropDown/SearchDropDown';
import { addEdgeToDecisionTreePath } from '../../../../actions/searchActions';
import { setPauseSubscription } from '../../../../actions/tools/CommonSubscriptionActions';
import { getSelectedOrderCustomerEmail, getSelectedOrderNumber } from '../../../../selectors/tools';
import { pauseSubscription } from '../../../../api/subscriptions';
import Loading, { LoadingWrapper } from 'components/LoadingComponent/LoadingComponent';
import { removeLastCharacterFromString } from '../../../../util';

/*
  This node is currently specific to Chegg due to their specific
  order system -- they do not adhere to the COI subscriptions.

  If another partner requires this feature, this can be quicky refined by
  grabbing the COI subscription state side by side with the Chegg-specific ones.
  Then handling it respective to the partner's order system.
*/

/* Added Hum Nutrition specific logic to handle the case where the user selects "No time duration" */
const DTPauseSubscriptionNode = () => {
  const dispatch = useDispatch();
  const decisionNode = useSelector(getDecisionNodeAtPath);
  const [ticketFieldValue, setTicketFieldValue] = useState('');
  const simplrAccountId = useSelector(getSimplrAccountIdOfSelectedTicket);
  const customerEmail = useSelector(getSelectedOrderCustomerEmail);
  const orderNumber = useSelector(getSelectedOrderNumber);

  const timePeriods = decisionNode?.timePeriods?.options || decisionNode?.timePeriods || []; // @TODO: Remove decisionNode.timePeriods check after verifying with Ops that the older versions of the trees have been updated.
  const [isLoading, setIsLoading] = useState(false);
  const isNoTimeDuration = decisionNode?.timePeriods?.isNoTimeDuration ?? false;

  const pauseSubscriptionNoTimeDuration = async () => {
    // Code for handling the pause subscription without selecting a pause duration.
    // Specific to the current partner (Hum Nutrition) and their order system.
    setIsLoading(true);
    try {
      const skipOrderPayload = {
        simplrAccountId,
        subscriptionId: orderNumber.toString(),
      };
      await pauseSubscription(skipOrderPayload);
      dispatch(addEdgeToDecisionTreePath('success'));
    } catch (error) {
      handleError({
        error,
      });
      dispatch(addEdgeToDecisionTreePath('failure'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isNoTimeDuration) {
      pauseSubscriptionNoTimeDuration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isNoTimeDuration, orderNumber]);

  const pauseSubscriptionHandler = async () => {
    setIsLoading(true);
    try {
      const { fieldValue: pauseIncrement, fieldTimeUnit } = ticketFieldValue.value;
      const incrementType = removeLastCharacterFromString(fieldTimeUnit).toUpperCase();
      const pauseSubscriptionPayload = {
        simplrAccountId,
        subscriptionId: orderNumber,
        pauseIncrement,
        incrementType,
        customerEmail,
      };
      const { startDate, endDate } = await pauseSubscription(pauseSubscriptionPayload);
      const pauseLabel = ticketFieldValue.label.toLowerCase();
      const pauseDuration = pauseIncrement > 1 ? pauseLabel : removeLastCharacterFromString(pauseLabel);

      dispatch(setPauseSubscription({ pauseDuration, startDate, endDate }));
      dispatch(addEdgeToDecisionTreePath('success'));
    } catch (error) {
      handleError({
        error,
      });
      dispatch(addEdgeToDecisionTreePath('failure'));
    } finally {
      setIsLoading(false);
    }
  };
  const onChangeHandler = (selectedItem) => {
    setTicketFieldValue(selectedItem);
  };

  const renderTitle = () => {
    if (decisionNode?.title) {
      return <Title>{decisionNode.title}</Title>;
    }

    return null;
  };

  const renderSubText = () => {
    if (decisionNode?.subText) {
      return <SubText>{decisionNode.subText}</SubText>;
    }

    return null;
  };

  return isLoading ? (
    <LoadingWrapper>
      <Loading />
    </LoadingWrapper>
  ) : (
    <TopContainer>
      <Container>
        {renderTitle()}
        {renderSubText()}
        <InnerContainer>
          <SearchDropDownComponent
            selectedId={ticketFieldValue}
            placeholder="Select pause duration..."
            options={timePeriods.map((item) => ({
              label: `${item.fieldValue} ${item.fieldTimeUnit}`,
              value: item,
            }))}
            onChange={onChangeHandler}
          />
          <SubmitButton disabled={!ticketFieldValue} active={!!ticketFieldValue} onClick={pauseSubscriptionHandler}>
            Pause Subscription
          </SubmitButton>
        </InnerContainer>
      </Container>
    </TopContainer>
  );
};

export default DTPauseSubscriptionNode;
